/**
 * paystubBatchStorage.js
 * ----------------------
 * Thin IndexedDB wrapper for offline-persisting the paycheck-stub capture
 * batch across tab reloads / crashes.  One object store, one record per
 * captured blob, keyed by {userId, shotId}.
 *
 * SECURITY POSTURE (be honest):
 * IndexedDB is protected by the browser sandbox + OS file permissions
 * (same-origin policy) but records themselves are NOT encrypted at rest.
 * We intentionally do not add an application-level encryption layer here
 * because the decryption key would need to live in localStorage — readable
 * by any XSS — which provides theatrical rather than real protection.  A
 * captured paystub image sitting for ~seconds between capture and upload
 * is an acceptable risk under the same origin already trusted with the
 * user's session cookie.
 */

const DB_NAME = "receipt-sheets-paystub-batch";
const DB_VERSION = 1;
const STORE = "shots";

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: "compositeKey" });
        store.createIndex("userId", "userId", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("IndexedDB open failed"));
  });
}

const compositeKey = (userId, shotId) => `${userId}::${shotId}`;

export async function putShot({ userId, shotId, blob, capturedAt }) {
  if (!userId || !shotId || !blob) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put({
        compositeKey: compositeKey(userId, shotId),
        userId,
        shotId,
        blob,
        capturedAt: capturedAt || Date.now(),
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // Silent failure — IndexedDB is best-effort; the in-memory batch still works.
  }
}

export async function deleteShot({ userId, shotId }) {
  if (!userId || !shotId) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(compositeKey(userId, shotId));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // Silent
  }
}

export async function listShotsForUser(userId) {
  if (!userId) return [];
  try {
    const db = await openDb();
    const rows = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).index("userId").getAll(userId);
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return rows;
  } catch {
    return [];
  }
}

export async function clearShotsForUser(userId) {
  if (!userId) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      const idx = tx.objectStore(STORE).index("userId");
      const req = idx.openCursor(IDBKeyRange.only(userId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // Silent
  }
}
